type SlidedownEvent = "slidedownShown" | "slidedownCancelClick" | "slidedownAllowClick" | "slidedownClosed";

type OneSignalInstance = {
    User: {
        addTags: (tags: Record<string, string | number | boolean>) => Promise<void>;
        removeTag: (key: string) => Promise<void>;
        getTags: () => Record<string, string>;
        addEmail: (email: string) => Promise<void>;
        removeEmail: (email: string) => Promise<void>;
        PushSubscription: {
            optIn: () => Promise<void>;
            optOut: () => Promise<void>;
            optedIn: boolean;
            id: string | null;
        };
    };
    login: (externalId: string) => Promise<void>;
    logout: () => Promise<void>;
    Notifications: {
        isPushSupported: () => boolean;
        permission: NotificationPermission;
        addEventListener: (event: "permissionChange", callback: (granted: boolean) => void) => void;
        removeEventListener: (event: "permissionChange", callback: (granted: boolean) => void) => void;
    };
    Slidedown: {
        promptPush: (options?: { force?: boolean }) => Promise<void>;
        addEventListener: (event: SlidedownEvent, callback: (...args: unknown[]) => void) => void;
        removeEventListener: (event: SlidedownEvent, callback: (...args: unknown[]) => void) => void;
    };
};

type OneSignalDeferred = ((OneSignal: OneSignalInstance) => void | Promise<void>)[];

type WindowWithOneSignal = {
    OneSignalDeferred?: OneSignalDeferred;
    __oneSignal?: OneSignalInstance | null;
    __oneSignalReady?: Promise<boolean>;
};

const ONESIGNAL_READY_TIMEOUT_MS = 5500;

// Serializes login/logout so all other SDK operations wait for identity to settle.
let identityPromise: Promise<void> = Promise.resolve();

const getOneSignalWindow = (): WindowWithOneSignal | null => {
    if (typeof window === "undefined") return null;
    return window as unknown as WindowWithOneSignal;
};

const withTimeout = async <T>(promise: Promise<T>, timeoutMs: number, fallback: T): Promise<T> => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;

    try {
        return await Promise.race([
            promise,
            new Promise<T>((resolve) => {
                timeoutId = setTimeout(() => resolve(fallback), timeoutMs);
            })
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

const waitForOneSignalInstance = async (): Promise<OneSignalInstance | null> => {
    const win = getOneSignalWindow();
    if (!win) return null;

    if (win.__oneSignal) {
        return win.__oneSignal;
    }

    if (win.__oneSignalReady) {
        const isReady = await withTimeout(
            win.__oneSignalReady.catch(() => false),
            ONESIGNAL_READY_TIMEOUT_MS,
            false
        );

        return isReady && win.__oneSignal ? win.__oneSignal : null;
    }

    return new Promise<OneSignalInstance | null>((resolve) => {
        let settled = false;
        const finish = (OneSignal: OneSignalInstance | null) => {
            if (settled) return;
            settled = true;
            clearTimeout(timeoutId);
            resolve(OneSignal);
        };

        const timeoutId = setTimeout(() => finish(win.__oneSignal ?? null), ONESIGNAL_READY_TIMEOUT_MS);

        win.OneSignalDeferred = win.OneSignalDeferred || [];
        win.OneSignalDeferred.push((OneSignal) => {
            win.__oneSignal = OneSignal;
            finish(OneSignal);
        });
    });
};

const runWithOneSignalAsync = async <T>(
    callback: (OneSignal: OneSignalInstance) => T | Promise<T>,
    fallback: T
): Promise<T> => {
    await identityPromise.catch(() => undefined);

    const OneSignal = await waitForOneSignalInstance();
    if (!OneSignal) return fallback;

    try {
        return await callback(OneSignal);
    } catch (err) {
        console.error("[OneSignal] Async operation failed:", err);
        return fallback;
    }
};

// iOS Safari supports push only in standalone (PWA home screen) mode.
// OneSignal SDK doesn't account for this yet (see PR #1441).
const isIOSNonStandalone = (): boolean => {
    if (typeof navigator === "undefined") return false;
    if (!/iPad|iPhone|iPod/.test(navigator.userAgent)) return false;
    const nav = navigator as Navigator & { standalone?: boolean };
    return nav.standalone !== true && !window.matchMedia("(display-mode: standalone)").matches;
};

const getFallbackPushSupport = (): boolean => {
    if (typeof window === "undefined") return false;

    if (!("serviceWorker" in navigator) || !("PushManager" in window)) {
        return false;
    }

    if (isIOSNonStandalone()) return false;

    const ua = navigator.userAgent || navigator.vendor || "";

    const inAppBrowserPatterns = [
        /Instagram/i,
        /FBAN|FBAV/i,
        /Twitter/i,
        /TikTok/i,
        /Snapchat/i,
        /Pinterest/i,
        /LinkedIn/i,
        /Line\//i,
        /KAKAOTALK/i,
        /WeChat|MicroMessenger/i
    ];

    for (const pattern of inAppBrowserPatterns) {
        if (pattern.test(ua)) {
            return false;
        }
    }

    return true;
};


export const setOneSignalExternalId = (userId: string): void => {
    identityPromise = identityPromise
        .catch(() => undefined)
        .then(async () => {
            const OneSignal = await waitForOneSignalInstance();
            if (!OneSignal) return;
            await withTimeout(OneSignal.login(userId), ONESIGNAL_READY_TIMEOUT_MS, undefined);
        })
        .catch((err) => {
            console.error("[OneSignal] Failed to set external id:", err);
        });
};

export const logoutOneSignal = (): void => {
    identityPromise = identityPromise
        .catch(() => undefined)
        .then(async () => {
            const OneSignal = await waitForOneSignalInstance();
            if (!OneSignal) return;
            await OneSignal.logout();
        })
        .catch((err) => {
            console.error("[OneSignal] Failed to logout:", err);
        });
};

export const setOneSignalTags = (tags: Record<string, string | number | boolean>): Promise<boolean> => {
    return runWithOneSignalAsync(async (OneSignal) => {
        await OneSignal.User.addTags(tags);
        return true;
    }, false);
};

export const isPushSupported = (): boolean => {
    if (isIOSNonStandalone()) return false;

    const win = getOneSignalWindow();

    if (win?.__oneSignal) {
        try {
            return win.__oneSignal.Notifications.isPushSupported();
        } catch (err) {
            console.error("[OneSignal] Failed to detect push support:", err);
        }
    }

    return getFallbackPushSupport();
};

export const addEmailToOneSignal = (email: string): Promise<boolean> => {
    if (!email) return Promise.resolve(false);

    return runWithOneSignalAsync(async (OneSignal) => {
        await OneSignal.User.addEmail(email);
        await OneSignal.User.addTags({ email_subscribed: "true" });
        return true;
    }, false);
};

export const disableEmailNotifications = (): Promise<boolean> => {
    return runWithOneSignalAsync(async (OneSignal) => {
        // Deliberately keep the email subscription itself (no removeEmail):
        // it is shared with other email flows (e.g. the renewal reminder),
        // and campaigns are targeted by tags anyway.
        await OneSignal.User.addTags({ email_subscribed: "false" });
        return true;
    }, false);
};

export const enableEmailNotifications = (email?: string): Promise<boolean> => {
    if (email) {
        return addEmailToOneSignal(email);
    }
    return Promise.resolve(false);
};

export type PushPermissionResult = "granted" | "denied" | "dismissed";

const syncPushSubscribedTag = async (
    OneSignal: OneSignalInstance,
    isSubscribed: boolean
): Promise<void> => {
    if (isSubscribed) {
        await OneSignal.User.addTags({ push_subscribed: "true" });
        return;
    }

    await OneSignal.User.removeTag("push_subscribed");
};

export const requestPushPermission = (): Promise<PushPermissionResult> => {
    if (typeof Notification === "undefined") return Promise.resolve("dismissed");
    if (Notification.permission === "denied") return Promise.resolve("denied");

    return runWithOneSignalAsync(async (OneSignal) => {
        if (!OneSignal.Notifications.isPushSupported()) return "dismissed";

        if (OneSignal.User.PushSubscription.optedIn) {
            await syncPushSubscribedTag(OneSignal, true);
            return "granted";
        }

        if (Notification.permission === "granted") {
            try { await OneSignal.User.PushSubscription.optIn(); } catch { }
            if (OneSignal.User.PushSubscription.optedIn) {
                await syncPushSubscribedTag(OneSignal, true);
                return "granted";
            }
            return "dismissed";
        }

        const result = await new Promise<PushPermissionResult>((resolve) => {
            let settled = false;

            const finish = (value: PushPermissionResult) => {
                if (settled) return;
                settled = true;
                OneSignal.Notifications.removeEventListener("permissionChange", onPermissionChange);
                OneSignal.Slidedown.removeEventListener("slidedownCancelClick", onSlidedownCancel);
                resolve(value);
            };

            const onPermissionChange = (granted: boolean) => {
                if (granted) {
                    finish("granted");
                } else {
                    finish(Notification.permission === "denied" ? "denied" : "dismissed");
                }
            };

            const onSlidedownCancel = () => {
                finish("dismissed");
            };

            OneSignal.Notifications.addEventListener("permissionChange", onPermissionChange);
            OneSignal.Slidedown.addEventListener("slidedownCancelClick", onSlidedownCancel);

            void OneSignal.Slidedown.promptPush({ force: true }).catch(() => {
                finish("dismissed");
            });
        });

        if (result === "granted") {
            if (!OneSignal.User.PushSubscription.optedIn) {
                try { await OneSignal.User.PushSubscription.optIn(); } catch { }
            }
            await syncPushSubscribedTag(OneSignal, true);
        }

        return result;
    }, "dismissed");
};

export const disablePushNotifications = (): Promise<boolean> => {
    return runWithOneSignalAsync(async (OneSignal) => {
        await OneSignal.User.PushSubscription.optOut();
        await syncPushSubscribedTag(OneSignal, false);
        return true;
    }, false);
};

export const enablePushNotifications = (): Promise<boolean> => {
    return runWithOneSignalAsync(async (OneSignal) => {
        await syncPushSubscribedTag(OneSignal, true);
        return true;
    }, false);
};

const timeStringToUnixTimestamp = (time: string): number => {
    const [hours, minutes] = time.split(":").map(Number);
    const date = new Date();
    date.setHours(hours, minutes, 0, 0);
    return Math.round(date.getTime() / 1000);
};

const unixTimestampToTimeString = (timestamp: number): string => {
    const date = new Date(timestamp * 1000);
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
};

export const setNotificationTime = (time: string): Promise<boolean> => {
    const unixTimestamp = timeStringToUnixTimestamp(time);
    return runWithOneSignalAsync(async (OneSignal) => {
        await OneSignal.User.addTags({ notification_time: unixTimestamp });
        return true;
    }, false);
};


export type NotificationSettings = {
    pushReminderEnabled: boolean;
    emailReminderEnabled: boolean;
    notificationTime: string | null;
};

const DEFAULT_NOTIFICATION_SETTINGS: NotificationSettings = {
    pushReminderEnabled: false,
    emailReminderEnabled: false,
    notificationTime: null
};

// Note: subscription renewal reminders are NOT handled here. They are a
// separate backend channel — consent lives in our DB (student_metadata, key
// renewal_reminder_subscribed) and the emails go out via SendGrid. The
// frontend toggle for it was removed; only these learning reminders remain.

export const getNotificationSettings = (): Promise<NotificationSettings> => {
    return runWithOneSignalAsync((OneSignal) => {
        const tags = OneSignal.User.getTags() || {};

        let notificationTime: string | null = null;
        if (tags.notification_time) {
            const timestamp = parseInt(tags.notification_time, 10);
            if (!isNaN(timestamp)) {
                notificationTime = unixTimestampToTimeString(timestamp);
            }
        }

        return {
            pushReminderEnabled: tags.push_subscribed === "true",
            emailReminderEnabled: tags.email_subscribed === "true",
            notificationTime
        };
    }, DEFAULT_NOTIFICATION_SETTINGS);
};
